import { useInfiniteQuery, useMutation, useQuery, UseMutationOptions, UseQueryOptions } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import secondsToMilliseconds from 'date-fns/secondsToMilliseconds';
import { dazzler, type EndpointResponse } from '~/data/dazzler-api';
import { queryKeyBuilder } from '~/data/utils/helpers/query-key.builder';
import type { ApiErrorBody } from '~/data/client-data-copy/errors';
import { ClientDataCopyStatus, type ClientDataCopyApi, type ClientDataCopyDto } from '~/data/openapi-client/index';
import type { PagingParams } from '~/data/utils/types';

export const clientDataCopyKeys = (() => {
  const base = 'client-data-copy';

  return {
    base: () => queryKeyBuilder(base),
    sourceCandidates: (clientId: string, search?: string) =>
      queryKeyBuilder(base, clientId, 'source-candidates', search),
    // The skip list is part of the identity of a preview: every count in the response honours it, so a
    // preview keyed only on the source client would serve stale numbers the moment a collision is ticked.
    preview: (clientId: string, sourceClientId?: string, skippedScopeIds?: string[]) =>
      queryKeyBuilder(base, clientId, 'preview', sourceClientId, [...(skippedScopeIds ?? [])].sort()),
    list: (clientId: string, params?: PagingParams) => queryKeyBuilder(base, clientId, 'list', params),
    copy: (clientId: string, copyId?: string) => queryKeyBuilder(base, clientId, 'copy', copyId),
    start: () => queryKeyBuilder(base, 'start'),
  };
})();

const SOURCE_CANDIDATE_PAGE_SIZE = 20;

/**
 * A copy is finished when it reaches one of these; polling must stop there rather than hammer a done job.
 *
 * `CopyingLogs` is NOT terminal despite its numeric value (6) sorting after `Completed` (5) - it is a member
 * appended to the enum later. Only ever compare this status as a string.
 */
const TERMINAL_STATUSES: ReadonlyArray<ClientDataCopyStatus> = [
  ClientDataCopyStatus.Completed,
  ClientDataCopyStatus.Failed,
];

export const isTerminalCopyStatus = (status?: ClientDataCopyStatus) => !!status && TERMINAL_STATUSES.includes(status);

/**
 * Candidate source clients for a copy ONTO `clientId`. Already access-filtered by the backend, ranked best
 * first, so the order is meaningful and must not be re-sorted client side.
 */
export function useListCopySourceCandidates(clientId: string, search?: string) {
  return useInfiniteQuery({
    queryKey: clientDataCopyKeys.sourceCandidates(clientId, search),
    queryFn: async function listCopySourceCandidates({ pageParam = 0, signal }) {
      const response = await dazzler.clientDataCopy.listSourceCandidates(
        { clientId, search, offset: pageParam, limit: SOURCE_CANDIDATE_PAGE_SIZE },
        { signal },
      );

      return response.data;
    },
    // Accumulate real pages: the next offset is how many rows we already hold, and we stop once that
    // reaches the server's total. (manage-clients' available-users-popup replaces the offset instead, so
    // its list never grows past one page - deliberately not copied.)
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((total, page) => total + page.items.length, 0);

      return loaded < lastPage.count ? loaded : undefined;
    },
    enabled: !!clientId,
  });
}

/**
 * What the copy WOULD do. Writes nothing, so it is modelled as a query even though the endpoint is a POST -
 * the body only carries the provisional skip list.
 */
export function useClientDataCopyPreview(
  clientId: string,
  sourceClientId: string | undefined,
  skippedScopeIds: string[] = [],
  options?: Omit<UseQueryOptions<EndpointResponse<ClientDataCopyApi['preview']>>, 'queryKey' | 'queryFn'>,
) {
  return useQuery({
    queryKey: clientDataCopyKeys.preview(clientId, sourceClientId, skippedScopeIds),
    queryFn: async function previewClientDataCopy({ signal }) {
      if (!sourceClientId) throw new Error('a source client is required to preview a data copy');

      const response = await dazzler.clientDataCopy.preview(
        { clientId, previewClientDataCopyValidator: { sourceClientId, skippedScopeIds } },
        { signal },
      );

      return response.data;
    },
    enabled: !!clientId && !!sourceClientId,
    ...options,
  });
}

/**
 * Every copy ever run onto this client, newest first. How an in-flight copy is found again after a reload,
 * when the page holds no copy id.
 */
export function useListClientDataCopies(
  clientId: string,
  params?: PagingParams,
  options?: Omit<UseQueryOptions<EndpointResponse<ClientDataCopyApi['list']>>, 'queryKey' | 'queryFn'>,
) {
  return useQuery({
    queryKey: clientDataCopyKeys.list(clientId, params),
    queryFn: async function listClientDataCopies({ signal }) {
      const response = await dazzler.clientDataCopy.list({ clientId, ...params }, { signal });

      return response.data;
    },
    enabled: !!clientId,
    ...options,
  });
}

export type StartClientDataCopyVariables = {
  /** ALWAYS the target - the client being written to. */
  clientId: string;
  sourceClientId: string;
  reason?: string;
  skippedScopeIds?: string[];
};

export const useStartClientDataCopy = (
  options?: Omit<
    UseMutationOptions<ClientDataCopyDto, AxiosError<ApiErrorBody>, StartClientDataCopyVariables>,
    'mutationFn' | 'mutationKey'
  >,
) =>
  useMutation({
    mutationFn: async function startClientDataCopy({
      clientId,
      sourceClientId,
      reason,
      skippedScopeIds,
    }: StartClientDataCopyVariables) {
      const response = await dazzler.clientDataCopy.start({
        clientId,
        startClientDataCopyValidator: { sourceClientId, reason, skippedScopeIds },
      });

      return response.data;
    },
    mutationKey: clientDataCopyKeys.start(),
    ...options,
  });

/**
 * Poll one copy. POLLING IS THE ONLY PROGRESS CHANNEL: the backend has no SignalR, no notifications table,
 * and no outbound mail, so a finished copy is only ever discovered by re-reading it.
 */
export function useGetClientDataCopy(
  clientId: string,
  copyId: string | undefined,
  options?: Omit<UseQueryOptions<EndpointResponse<ClientDataCopyApi['get']>>, 'queryKey' | 'queryFn'>,
) {
  return useQuery({
    queryKey: clientDataCopyKeys.copy(clientId, copyId),
    queryFn: async function getClientDataCopy({ signal }) {
      if (!copyId) throw new Error('a copy id is required to read a data copy');

      const response = await dazzler.clientDataCopy.get({ clientId, copyId }, { signal });

      return response.data;
    },
    enabled: !!clientId && !!copyId,
    refetchInterval: (data) => (isTerminalCopyStatus(data?.status) ? false : secondsToMilliseconds(5)),
    ...options,
  });
}
